import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import { BarcodeExportConfigurationContext } from '../../../../../context/BarcodeExportConfigurationContext';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { LotContext } from '../../../../../context/LotContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import Provider from '../../../../../providers/common/Provider';
import { useToast } from '../../../../../providers/ToastProvider';
import { BarcodeExportConfigurationRequest } from '../../../../../requests/barcodeExportConfiguration.request';
import { useRequest } from '../../../../../requests/common/requestHook';
import {
  BarcodeExportConfiguration,
  BarcodeExportConfigurationField,
  BarcodeExportConfigurationFormat,
} from '../../../../../types/barcode_export_configuration';
import { BarcodeExportEntityType } from '../../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { Product } from '../../../../../types/product';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../types/translation/Translator';
import { ExportUtil } from '../../../../../util/export.util';
import { Routing } from '../../../../../util/routes.util';
import { DropdownSearchSelect } from '../../../../components/Dropdown';
import { TextInput } from '../../../../components/TextInput';
import { ToastType } from '../../../../components/Toast';
import LoadingScreen from '../../../Common/LoadingScreen';
import { getBarcodeDataKeys, PdfFieldEntity } from '../../../PdfTemplateEditor/data/PdfDataKey';
import {
  PdfBorderStyle,
  PdfComponentType,
  PdfFontFamily,
  PdfImageFit,
  PdfOrientation,
  PdfPageFormat,
  PdfRepeat,
  PdfTemplateProperties,
  PdfTextAlign,
  PdfVerticalAlign,
} from '../../../PdfTemplateEditor/PdfComponent';
import PdfTemplatePropertiesEditor from '../../../PdfTemplateEditor/PdfTemplatePropertiesEditor';

interface BarcodeExportConfigurationLabelEditorProps {}

export default function BarcodeExportConfigurationLabelEditor() {
  return (
    <Provider customField productMasterData container lot barcodeExportConfiguration>
      <Element />
    </Provider>
  );
}

export function Element({}: BarcodeExportConfigurationLabelEditorProps) {
  const id = useParams()['id'] || '';

  const { showToast } = useToast();
  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { barcodeExportConfigurations, barcodeExportConfigurationsLoading, setBarcodeExportConfigurations } =
    useContext(BarcodeExportConfigurationContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { lots } = useContext(LotContext);

  const [update, { loading }] = useRequest(BarcodeExportConfigurationRequest.update);
  const [generate, { loading: generateLoading }] = useRequest(BarcodeExportConfigurationRequest.generate);

  const { customFields } = useContext(CustomFieldContext);

  const configuration = useMemo(() => {
    return barcodeExportConfigurations.get(id);
  }, [id, barcodeExportConfigurations]);

  const [input, setInput] = useState<PdfTemplateProperties>(
    configuration?.label?.properties || {
      components: [],
      document: {
        orientation: PdfOrientation.landscape,
        pageFormat: PdfPageFormat.custom,
        size: { width: 50, height: 25 },
        font: { family: PdfFontFamily.helvetica, size: 3, color: '#111111' },
      },
    },
  );

  useEffect(() => {
    if (configuration?.label) setInput(configuration.label?.properties);
  }, [configuration]);

  const [previewProduct, setPreviewProduct] = useState<Product>(
    new Product({
      companyId: currentCompany.id,
      stockLocationId: 'stock-location-id',
      productMasterDataId: 'product-master-data-id',
    }),
  );

  if (barcodeExportConfigurationsLoading) return <LoadingScreen />;

  if (!configuration) return null;

  const dataKeys = getBarcodeDataKeys(customFields);

  const previewPmd = productMasterData.get(previewProduct.productMasterDataId);

  const preview = (
    <>
      <DropdownSearchSelect
        label={translate({ key: TranslationKey.productMasterData })}
        items={[...productMasterData.values()]}
        value={previewPmd}
        onSelect={p =>
          setPreviewProduct(
            product =>
              new Product({ ...product, serial: undefined, lotId: undefined, productMasterDataId: p?.id || '' }),
          )
        }
        textBuilder={p => p.productNumber}
        placeholder={'Select preview pmd'}
      />
      {previewPmd?.serialManaged && (
        <TextInput
          value={previewProduct.serial}
          label={translate({ key: TranslationKey.serial })}
          placeholder={translate({ key: TranslationKey.serial })}
          onChange={value => setPreviewProduct(product => new Product({ ...product, serial: value }))}
        />
      )}
      {previewPmd?.lotManaged && (
        <DropdownSearchSelect
          value={lots.get(previewProduct.lotId || '')}
          items={Array.from(lots.values()).filter(l => l.productMasterDataId === previewPmd.id)}
          onSelect={l => setPreviewProduct(product => new Product({ ...product, lotId: l?.id }))}
          placeholder={translate({ key: TranslationKey.lot })}
          label={translate({ key: TranslationKey.lot })}
          textBuilder={l => l.number}
        />
      )}
    </>
  );

  return (
    <PdfTemplatePropertiesEditor<BarcodeExportConfiguration>
      properties={input}
      previewProperties={{
        preview,
        handleSample: async properties => {
          if (!previewProduct.productMasterDataId) return;
          // TODO:
          const response = await generate({
            entityIds: [],
            entityType: BarcodeExportEntityType.product,
            configurationId: configuration.id,
            sampleLabel: {
              entity: previewProduct,
              configuration: new BarcodeExportConfiguration({ ...configuration, label: { properties } }),
            },
          });
          ExportUtil.downloadExport(response);
        },
      }}
      onDone={async properties => {
        setInput(properties);
        configuration.label = { properties };
        const response = await update({ data: [configuration] });
        if (!response) return;

        setBarcodeExportConfigurations(
          new Map(barcodeExportConfigurations).set(response[0].id, new BarcodeExportConfiguration(response[0])),
        );
        showToast({
          type: ToastType.success,
          title: translate({ key: TranslationKey.barcodeExportConfigurationUpdatedSuccessfullyToast }),
        });
        navigate(Routing.settings.barcodes.update(configuration.id));
      }}
      dataKeys={dataKeys}
      imageDataKeys={[]}
      tableProviders={[]}
      settings={{
        repeatModes: [],
        pageFormats: [PdfPageFormat.custom],
        orientations: [],
        barcodeExportEntries: [...configuration.entries],
        defaultGridSizeMm: 0.5,
        defaultZoom: 3,
      }}
      defaultComponents={{
        staticText: () => ({
          id: v4(),
          border: { enabled: false, width: 0.5, style: PdfBorderStyle.solid, color: '#000000' },
          type: PdfComponentType.staticText,
          x: 0,
          y: 0,
          width: 19,
          height: 5,
          text: 'Static text',
          repeatMode: PdfRepeat.always,
          align: PdfTextAlign.left,
          valign: PdfVerticalAlign.top,
        }),
        barcode: () => ({
          id: v4(),
          type: PdfComponentType.barcode,
          x: 0,
          y: 0,
          width: 10,
          height: 5,
          key: { entityType: PdfFieldEntity.product, property: '' },
          repeatMode: PdfRepeat.always,
          entry: {
            format: BarcodeExportConfigurationFormat.code128,
            height: 5,
            horizontalMargin: 2,
            verticalMargin: 2,
            includeText: true,
            field: BarcodeExportConfigurationField.productNumber,
            id: v4(),
          },
        }),
        image: () => ({
          id: v4(),
          type: PdfComponentType.image,
          x: 0,
          y: 0,
          width: 5,
          height: 5,
          src: undefined,
          fit: PdfImageFit.contain,
          opacity: 1,
          repeat: true,
          repeatMode: PdfRepeat.always,
        }),
        field: () => ({
          id: v4(),
          border: { enabled: false, width: 0.5, style: PdfBorderStyle.solid, color: '#000000' },
          type: PdfComponentType.field,
          x: 0,
          y: 0,
          width: 10,
          height: 5,
          key: dataKeys[0],
          repeatMode: PdfRepeat.always,
          align: PdfTextAlign.left,
          valign: PdfVerticalAlign.top,
        }),
      }}
    />
  );
}
